import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccountGroupAndType,
  AccountModel,
  ChartOfAccountListModel,
  GroupListModel,
  SideListModel,
} from '@app/core/Models';
import { CommonService } from '@app/core/Services';
import { environment } from '@environments/environment';
import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(private http: HttpClient, private commonService: CommonService) {}

  createAccount(account: AccountModel): Observable<any> {
    const headers = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(
      `${environment.apiVersionUrl}Account/save`,
      this.commonService.trimObjectSpace(JSON.stringify(account)),
      headers
    );
  }

  createContact(account: AccountModel): Observable<any> {
    const headers = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(
      `${environment.apiVersionUrl}Account/contactSave`,
      this.commonService.trimObjectSpace(JSON.stringify(account)),
      headers
    );
  }

  getTermsList(): Observable<Array<SideListModel>> {
    return this.http.get<Array<SideListModel>>(
      `${environment.apiVersionUrl}Term/all`
    );
  }

  getBusinessTypeList(): Observable<Array<SideListModel>> {
    return this.http.get<Array<SideListModel>>(
      `${environment.apiVersionUrl}BusinessType/all`
    );
  }

  getDataByAccountId(accountId: Guid): Observable<AccountModel> {
    return this.http.get<AccountModel>(
      `${environment.apiVersionUrl}Account/get/${accountId}`
    );
  }

  getChartOfAccountList(moduleId?: number): Observable<SideListModel> {
    const entityType = this.commonService.getEntityTypeId(moduleId);

    return this.http.get<SideListModel>(
      `${environment.apiVersionUrl}ChartOfAccount/list/${entityType}`
    );
  }

  getChartOfAccountGroupList(): Observable<SideListModel> {
    return this.http.get<SideListModel>(
      `${environment.apiVersionUrl}ChartOfAccount/getAllGroups`
    );
  }

  getChartOfAccountTypeList(groupId?: number): Observable<SideListModel> {
    return this.http.get<SideListModel>(
      `${environment.apiVersionUrl}ChartOfAccount/getAllTypes/${groupId}`
    );
  }

  getChartOfAllAccountsList(
    groupId?: number,
    typeId?: number
  ): Observable<SideListModel> {
    return this.http.get<SideListModel>(
      `${environment.apiVersionUrl}ChartOfAccount/getAllAccounts/${groupId}/${typeId}`
    );
  }

  getFundTypeList(): Observable<SideListModel> {
    return this.http.get<SideListModel>(
      `${environment.apiVersionUrl}FundType/all`
    );
  }

  getJournalAccounts(fundId?: any): Observable<Array<GroupListModel>> {
    const url = fundId
      ? `${environment.apiVersionUrl}Account/groupFilteredAccountsByFundId/${fundId}`
      : `${environment.apiVersionUrl}Account/groupFilteredAccounts`;

    return this.http.get<Array<GroupListModel>>(url);
  }

  deleteAccount(accountIds?: Array<Guid>): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: this.commonService.trimObjectSpace(JSON.stringify(accountIds)),
    };
    return this.http.delete<any>(
      `${environment.apiVersionUrl}Account/delete`,
      options
    );
  }

  copyAccount(accountIds?: Array<Guid>): Observable<any> {
    const headers = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.put<any>(
      `${environment.apiVersionUrl}Account/copy`,
      this.commonService.trimObjectSpace(JSON.stringify(accountIds)),
      headers
    );
  }

  archiveAndRestoreAccount(
    accountIds?: Array<Guid>,
    isArchive?: boolean
  ): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.patch<any>(
      `${environment.apiVersionUrl}Account/archive/${isArchive}`,
      this.commonService.trimObjectSpace(JSON.stringify(accountIds)),
      options
    );
  }

  getSupplierList(entityId: number): Observable<Array<SideListModel>> {
    return this.http.get<Array<SideListModel>>(
      `${environment.apiVersionUrl}Account/all/AccountType/${entityId}`
    );
  }

  getAccountTypeList(
    accountTypeId: number
  ): Observable<Array<ChartOfAccountListModel>> {
    return this.http.get<Array<ChartOfAccountListModel>>(
      `${environment.apiVersionUrl}ChartOfAccount/getAll/accountType/${accountTypeId}`
    );
  }

  getFundNameList(): Observable<SideListModel> {
    return this.http.get<SideListModel>(`${environment.apiVersionUrl}Fund/all`);
  }

  getAccountGroupAndType(
    accountGroupAndTypeId: Guid
  ): Observable<Array<AccountGroupAndType>> {
    return this.http.get<Array<AccountGroupAndType>>(
      `${environment.apiVersionUrl}Account/getAccountGroupAndType/${accountGroupAndTypeId}`
    );
  }

  getExplain(id: Guid): Observable<Array<any>> {
    return this.http.get<Array<any>>(
      `${environment.apiVersionUrl}OpeningBalance/getExplain/${id}`
    );
  }

  getOpeningBalanceExplainList(accountTypeId: number): Observable<Array<any>> {
    return this.http.get<Array<any>>(
      `${environment.apiVersionUrl}openingBalance/explainList/${accountTypeId}`
    );
  }

  getExplainListBasedOnAccountingPeriod(
    accountTypeId: number,
    accountingPeriodId: Guid
  ): Observable<Array<any>> {
    return this.http.get<Array<any>>(
      `${environment.apiVersionUrl}openingBalance/explainListBasedOnAccountingPeriod/${accountTypeId}/${accountingPeriodId}`
    );
  }

  getFixedAssetAdditionList(): Observable<Array<GroupListModel>> {
    return this.http.get<Array<GroupListModel>>(
      `${environment.apiVersionUrl}account/getGroupFixedAssetAddition`
    );
  }

  getAccountsForBankFeed(): Observable<Array<SideListModel>> {
    return this.http.get<Array<SideListModel>>(
      `${environment.apiVersionUrl}Account/getAccountsForBankFeed`
    );
  }
}
